/**
 * FlowAI Cockpit — System-Overview mit 4 Sektionen:
 *  1. KPI-Tiles (Events 24h, Pending Approvals, Sandbox-Status, Worker-Heartbeat)
 *  2. Letzte Events (10)
 *  3. Pending Approvals (Top 5)
 *  4. Workspace-Liste mit Status-Pillen
 *
 * @page /flowai
 * @since v2.484.0
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Card, Badge, Button } from '../../components/ui';
import FlowAIBreadcrumb from '../../components/flowai/FlowAIBreadcrumb';

const API = import.meta.env.VITE_API_URL || '';

function Pill({ tone = 'gray', children }) {
  const tones = {
    green: 'bg-green-50 text-green-700 border-green-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${tones[tone]}`}>{children}</span>;
}

function Tile({ label, value, hint, tone = 'gray' }) {
  return (
    <Card>
      <div className="text-[11px] uppercase tracking-wide text-[var(--text-tertiary,#6B7280)] mb-1">{label}</div>
      <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{value}</div>
      {hint && <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1 flex items-center gap-2">{hint}</div>}
    </Card>
  );
}

export default function FlowAICockpit() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/flowai/cockpit`, { credentials: 'include' });
      const d = await r.json();
      setData(d);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 text-sm text-[var(--text-secondary,#4B5563)]">Lade Cockpit…</div>;
  if (!data) return <div className="p-6 text-sm text-red-600">Cockpit konnte nicht geladen werden.</div>;

  const queueStats = data.queue_stats || {};
  const events24 = Number(data.events_24h) || 0;
  const pendingApprovals = data.pending_approvals ?? 0;
  const workspacesList = Array.isArray(data.workspaces) ? data.workspaces : [];
  const sandboxCount = workspacesList.filter((w) => w.sandbox_mode === true).length;
  const sandboxOn = sandboxCount > 0;
  const hbAgo = data.worker?.last_beat_seconds_ago ?? null;
  const hbTone = hbAgo == null ? 'red' : hbAgo < 30 ? 'green' : hbAgo < 120 ? 'amber' : 'red';

  return (
    <div className="p-6">
      <FlowAIBreadcrumb trail={[{ label: 'Cockpit' }]} />
      <PageHeader
        title="FlowAI Cockpit"
        subtitle="AI-Automation Foundation — Events, Approvals, Workspaces"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => navigate('/flowai/admin/safety')}>🧪 Sicherheit</Button>
            <Button variant="ghost" onClick={() => navigate('/flowai/admin/email')}>SMTP-Config</Button>
            <Button onClick={load} variant="secondary">Aktualisieren</Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Tile label="Events (24h)" value={events24} />
        <Tile label="Pending Approvals" value={pendingApprovals} tone={pendingApprovals > 0 ? 'amber' : 'gray'} />
        <Tile
          label="Sandbox-Mode"
          value={sandboxOn ? 'ON' : 'OFF'}
          hint={<Pill tone={sandboxOn ? 'amber' : 'green'}>{sandboxOn ? `Sandbox in ${sandboxCount} Workspace${sandboxCount === 1 ? '' : 's'} aktiv` : 'alle Live'}</Pill>}
        />
        <Tile
          label="Worker Heartbeat"
          value={hbAgo == null ? '–' : `${hbAgo}s`}
          hint={<Pill tone={hbTone}>{hbAgo == null ? 'offline' : 'online'}</Pill>}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Letzte Aktionen" actions={<Button variant="ghost" onClick={() => navigate('/flowai/audit')}>Audit</Button>}>
          <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
            {(Array.isArray(data.recent_audit) ? data.recent_audit : []).slice(0, 10).map((ev) => (
              <div key={ev.id} className="py-2 flex items-center gap-3 text-[12px]">
                <span className="font-mono text-[var(--text-tertiary,#6B7280)]">#{ev.id}</span>
                <span className="font-medium">{ev.action_type}</span>
                <Pill tone={ev.approval_status === 'approved' ? 'green' : ev.approval_status === 'rejected' ? 'red' : 'gray'}>{ev.approval_status}</Pill>
                <span className="text-[var(--text-tertiary,#6B7280)] ml-auto">{new Date(ev.created_at).toLocaleTimeString()}</span>
              </div>
            ))}
            {(!Array.isArray(data.recent_audit) || data.recent_audit.length === 0) && (
              <div className="py-6 text-center text-[12px] text-[var(--text-tertiary,#6B7280)]">Keine Aktionen</div>
            )}
          </div>
        </Card>

        <Card title="Queue-Status" actions={<Button variant="ghost" onClick={() => navigate('/flowai/events')}>Events</Button>}>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{queueStats.queued || 0}</div>
              <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">Queued</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{queueStats.processing || 0}</div>
              <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">Processing</div>
            </div>
            <div>
              <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{queueStats.failed || 0}</div>
              <div className="text-[11px] text-[var(--text-tertiary,#6B7280)] mt-1">Failed</div>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Workspaces" className="mt-4" actions={<Button onClick={() => navigate('/flowai/workspaces')}>Verwalten</Button>}>
        <div className="divide-y divide-[var(--border-subtle,#E5E7EB)]">
          {workspacesList.map((ws) => (
            <div key={ws.id} className="py-2 flex items-center gap-3 text-[12px]">
              <span className="font-medium">{ws.name}</span>
              <Pill tone={ws.pilot_mode ? 'amber' : 'green'}>{ws.pilot_mode ? 'Pilot' : 'Live'}</Pill>
              <Pill tone={ws.sandbox_mode ? 'amber' : 'gray'}>Sandbox: {ws.sandbox_mode ? 'ON' : 'OFF'}</Pill>
              <span className="ml-auto text-[var(--text-tertiary,#6B7280)]">{ws.actions_today || 0} Aktionen heute · {ws.approved_today || 0} freigegeben</span>
              <Button variant="ghost" onClick={() => navigate(`/flowai/workspaces/${ws.id}`)}>Öffnen</Button>
            </div>
          ))}
          {workspacesList.length === 0 && (
            <div className="py-6 text-center text-[12px] text-[var(--text-tertiary,#6B7280)]">Keine Workspaces</div>
          )}
        </div>
      </Card>
    </div>
  );
}
